import { useState, useContext, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { AuthContext } from '@lib/context/AuthContextType';
import { portalLogin, getPortalGoogleUrl } from '@lib/api/portal';
import type { User } from '@/types/Auth.types';

export const PortalLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const ctx = useContext(AuthContext)!;
  const navigate = useNavigate();
  const [params] = useSearchParams();

  // If already authenticated (e.g. preview token loaded async), redirect to portal
  useEffect(() => {
    if (!ctx.loading && ctx.isAuthenticated && ctx.user) {
      const dest = ctx.user.role === 'employee' ? '/portal/employee' : '/portal/client';
      navigate(dest, { replace: true });
    }
  }, [ctx.loading, ctx.isAuthenticated, ctx.user, navigate]);

  const errorMap: Record<string, string> = {
    google_failed: 'Google sign-in failed. Try again.',
    no_invite: 'No invitation found for that Google account.',
    account_exists: 'An admin account already exists with that email. Use email/password login instead.',
  };
  const urlError = errorMap[params.get('error') || ''] ?? '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const result = await portalLogin(email, password);
      ctx.setSession(result.accessToken, result.refreshToken, result.user as User);
      navigate(result.user.role === 'employee' ? '/portal/employee' : '/portal/client');
    } catch (err: unknown) {
      setError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Login failed');
    } finally {
      setBusy(false);
    }
  };

  const handleGoogle = async () => {
    setBusy(true);
    try {
      const url = await getPortalGoogleUrl();
      window.location.href = url;
    } catch {
      setError('Google sign-in not available');
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF6F0] flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img src="/icon-192.png" alt="OchrePoint" className="w-16 h-16 rounded-2xl mx-auto mb-3 shadow-sm" />
          <h1 className="text-2xl font-bold text-[#2C1810]">OchrePoint Contracting</h1>
          <p className="text-[#C77D3F] text-xs font-semibold tracking-wide mt-0.5 uppercase">Built on Skill, Finished with Pride</p>
          <p className="text-gray-500 text-sm mt-2">Sign in to your portal</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          {(error || urlError) && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error || urlError}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/40" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/40" />
            </div>
            <button type="submit" disabled={busy}
              className="w-full bg-[#C77D3F] text-white rounded-lg py-2.5 text-sm font-semibold hover:bg-[#b36e35] disabled:opacity-50">
              {busy ? 'Signing in…' : 'Sign In'}
            </button>
            <p className="text-center">
              <Link to="/portal/forgot" className="text-xs text-gray-400 hover:text-[#C77D3F]">Forgot password?</Link>
            </p>
          </form>

          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
            <div className="relative flex justify-center text-xs text-gray-400"><span className="bg-white px-2">or</span></div>
          </div>

          <button onClick={handleGoogle} disabled={busy}
            className="w-full border border-gray-300 rounded-lg py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 flex items-center justify-center gap-2">
            <svg width="16" height="16" viewBox="0 0 48 48"><path fill="#4285F4" d="M47.5 24.5c0-1.6-.1-3.2-.4-4.7H24v9h13.2c-.6 3-2.4 5.6-5 7.3v6h8.1c4.7-4.4 7.2-10.8 7.2-17.6z"/><path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.7l-8.1-6c-2.2 1.5-5 2.3-7.8 2.3-6 0-11-4-12.8-9.4H2.9v6.2C6.9 43.4 14.9 48 24 48z"/><path fill="#FBBC05" d="M11.2 29.2c-.5-1.5-.8-3-.8-4.7s.3-3.2.8-4.7v-6.2H2.9C1 17.2 0 20.5 0 24s1 6.8 2.9 9.4l8.3-4.2z"/><path fill="#EA4335" d="M24 9.5c3.3 0 6.3 1.1 8.6 3.3l6.4-6.4C35.9 2.1 30.5 0 24 0 14.9 0 6.9 4.6 2.9 11.6l8.3 6.2c1.8-5.4 6.8-8.3 12.8-8.3z"/></svg>
            Continue with Google
          </button>
        </div>

        <p className="text-center text-sm text-gray-500 mt-4">
          Have an invite code?{' '}
          <Link to="/portal/register" className="text-[#C77D3F] hover:underline">Register here</Link>
        </p>

        {/* Public demo — hidden from real clients; append ?try=1 to show */}
        {params.get('try') === '1' && (<>
        <div className="mt-3 mx-auto max-w-xs rounded-xl border border-gray-200 bg-white/80 px-4 py-3">
          <p className="text-[11px] font-bold text-gray-500 text-center uppercase tracking-wide mb-2">Try the demo — fake data, nothing saved</p>
          <div className="flex gap-2">
            <a href="/portal/employee?demo=employee"
              className="flex-1 h-9 rounded-lg bg-[#2C1810] text-white text-xs font-bold flex items-center justify-center gap-1">
              <span className="material-symbols-outlined text-base">construction</span> Worker demo
            </a>
            <a href="/portal/client?demo=client"
              className="flex-1 h-9 rounded-lg border border-[#2C1810]/20 text-[#2C1810] text-xs font-bold flex items-center justify-center gap-1">
              <span className="material-symbols-outlined text-base">person</span> Client demo
            </a>
          </div>
        </div>

        {/* No login needed for the code viewer */}
        <Link to="/code"
          className="mt-3 mx-auto flex items-center justify-center gap-2 max-w-xs rounded-xl border border-[#C77D3F]/30 bg-white/80 px-4 py-2.5 text-sm font-semibold text-[#C77D3F] hover:bg-white">
          <span className="material-symbols-outlined text-lg">menu_book</span>
          BC Building Code 2024 — no sign-in needed
        </Link>
        </>)}

        <p className="text-center text-[11px] text-gray-400 mt-6">
          Need help? Call us at <a href="tel:+12505100677" className="text-[#C77D3F]">(250) 510-0677</a>
        </p>
      </div>
    </div>
  );
};
